/* eslint-disable react/no-danger */
import React, { useCallback, useEffect, useState } from 'react';
import Loading from '../loading.js';
import InputWithClear from '../InputComponents/InputWithClear';
import FigmaButton from '../FigmaButton';
import FigmaButtonRow from '../FigmaButtonRow';
import ButtonColorsEnum from '../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../Enums/IconPositionEnum';
import ErrorMessage from '../ErrorMessage';
import dateTimeHelper from '../../Helpers/datetimeHelper';
import '../../Css/CommentsComponent.css';

// Descriptor-driven panel: an append-only list of timestamped entries + an inline add box. All machinery
// (fetch/refresh, add toggle, field render, save, toasts) lives here; each use-site declares its differences
// in a static `description`, and the render location passes only `context` (live ids / status).
//
// `description` fields:
//   title(context?)      — panel heading (value or fn).
//   errorCode            — suffix for the load-failure toast.
//   bodyAsHtml           — render bodies as HTML (dangerouslySetInnerHTML). Optional, default false.
//   populate(context)    — fetches the raw rows (Promise).
//   convert(rows)        — maps raw rows to display items.
//   renderBody(entry, context) — the entry body (HTML string when bodyAsHtml).
//   metaAttribution      — meta line { attributionPrefix, authorNameKey, timestampKey, authorIdKey? },
//                          shown as "{prefix} {name} on {date}" (name omitted when absent). Optional.
//   canAdd(context)      — show the add toggle/form (default: true).
//   addDisabledReason(context) — instead of canAdd: the add toggle is always shown, and a returned text greys it
//                          out with that text as its tooltip (falsy enables it). Optional.
//   stateMessage(context)— contextual notice in the list area (disabled / not-yet-Won / …); falsy shows the
//                          default 'No entries yet' when the list is empty.
//   addEntry(inputValue, context, noteValue) — the add call (Promise; a resolved { error } is a handled failure).
//                          noteValue is the noteField's value, or undefined without one.
//   addField             — the add input for InputWithClear:
//                          { type, initialValue, placeholder, label, className, isValid } (type/placeholder/label may be fns).
//   noteField            — an optional second, free-text input under addField that never blocks the save:
//                          { type, initialValue, placeholder, className } (type/placeholder may be fns). Optional.
export default function GeneralEntryLogFigma({ description, context = {} }) {
  const desc = description;
  const addField = desc.addField;
  const noteField = desc.noteField;

  //#region State & data loading
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [addPanelInputValue, setAddPanelInputValue] = useState(addField ? addField.initialValue : '');
  const [noteValue, setNoteValue] = useState(noteField ? noteField.initialValue : undefined);
  const [saving, setSaving] = useState(false);

  const tryResolveFunction = (maybeFunction, ctx) =>
    typeof maybeFunction === 'function' ? maybeFunction(ctx) : maybeFunction;

  // contextKey gates effect re-runs; context itself is read fresh.
  const contextKey = JSON.stringify(context);

  const loadEntriesAsync = useCallback(() => {
    setLoading(true);
    Promise.resolve(desc.populate(context))
      .then((rows) => {
        setEntries(desc.convert(rows) || []);
        setLoading(false);
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage(`Error! ErrorCode: ${desc.errorCode || 'GELF49'}`);
        setLoading(false);
      });
  }, [desc, contextKey]);

  useEffect(() => {
    loadEntriesAsync();
  }, [loadEntriesAsync]);
  //#endregion

  //#region Add-box actions
  // A description with addDisabledReason keeps its toggle visible and greys it out; canAdd hides it.
  const addDisabledReason = desc.addDisabledReason ? desc.addDisabledReason(context) : null;
  const showAddToggle = desc.addDisabledReason ? true : desc.canAdd ? desc.canAdd(context) : true;
  const canAdd = showAddToggle && !addDisabledReason;
  // Contextual notice, rendered in the list area so it never hides the add form (governed solely by canAdd).
  const stateMsg = desc.stateMessage ? desc.stateMessage(context) : null;

  const resetInput = () => {
    setAddPanelInputValue(addField ? addField.initialValue : '');
    setNoteValue(noteField ? noteField.initialValue : undefined);
  };
  const toggleAdd = () => {
    setIsAddPanelOpen(!isAddPanelOpen);
    resetInput();
  };
  const closeAdd = () => {
    setIsAddPanelOpen(false);
    resetInput();
  };

  const handleSubmit = () => {
    setSaving(true);
    Promise.resolve(desc.addEntry(addPanelInputValue, context, noteValue))
      .then((response) => {
        setSaving(false);
        if (response.error) {
          ErrorMessage.SetErrorMessage(response.error);
          return;
        }
        ErrorMessage.SetSuccessMessage('New entry added successfully.');
        closeAdd();
        loadEntriesAsync();
      })
      .catch(() => {
        setSaving(false);
        ErrorMessage.SetErrorMessage(`Error! ErrorCode: GELF100 - ${desc.errorCode}`);
      });
  };
  //#endregion

  //#region Rendering
  // "{attributionPrefix} {name} on {date}" from a metaAttribution config; omits the name when absent
  // (authorIdKey gives a "User {id}" fallback).
  const buildAttribution = (entry, cfg) => {
    const name = entry[cfg.authorNameKey] || (cfg.authorIdKey ? `User ${entry[cfg.authorIdKey]}` : null);
    const date = dateTimeHelper.toYYYYsMMsDDsHHsMM(entry[cfg.timestampKey]);
    return name ? `${cfg.attributionPrefix} ${name} on ${date}` : date;
  };

  const renderHeaderActions = () => {
    if (!showAddToggle || !addField) return null;
    return (
      <FigmaButtonRow
        className='panel-corner-actions'
        buttons={[
          {
            iconName: isAddPanelOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-plus-circle',
            type: ButtonTypeEnum.NormalIconOnly,
            color: isAddPanelOpen ? ButtonColorsEnum.Secondary : ButtonColorsEnum.Primary,
            iconPosition: IconPositionEnum.Center,
            disabled: !!addDisabledReason,
            onClick: toggleAdd,
            tooltip: addDisabledReason || (isAddPanelOpen ? 'Hide' : 'Add'),
          },
        ]}
      />
    );
  };

  const renderAddBox = () => {
    if (!canAdd || !isAddPanelOpen || !addField) {
      return null;
    }

    const label = tryResolveFunction(addField.label, context);
    const field = (
      <InputWithClear
        type={tryResolveFunction(addField.type, context)}
        fieldName='entryInput'
        value={addPanelInputValue}
        onChange={(name, newValue) => setAddPanelInputValue(newValue)}
        placeholder={tryResolveFunction(addField.placeholder, context)}
        className={addField.className || 'cmt-textarea'}
        labelIsDisabled
        disableClearButton
      />
    );
    const isValid = addField.isValid ? addField.isValid(addPanelInputValue) : true;

    return (
      <div className='cmt-add'>
        {label ? (
          <label className='cmt-confirm-label'>
            {field}
            <span>{label}</span>
          </label>
        ) : (
          field
        )}
        {noteField ? (
          <div className='cmt-add-note'>
            <InputWithClear
              type={tryResolveFunction(noteField.type, context)}
              fieldName='entryNote'
              value={noteValue}
              onChange={(name, newValue) => setNoteValue(newValue)}
              placeholder={tryResolveFunction(noteField.placeholder, context)}
              className={noteField.className || 'cmt-textarea'}
              labelIsDisabled
              disableClearButton
            />
          </div>
        ) : null}
        <div className='cmt-add-actions'>
          <FigmaButton
            label={'Save'}
            iconName={'fa-solid fa-paper-plane'}
            iconPosition={IconPositionEnum.Left}
            color={ButtonColorsEnum.Primary}
            type={ButtonTypeEnum.NormalNormal}
            className='cmt-save-btn'
            disabled={!isValid || saving}
            onClick={handleSubmit}
          />
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className='cmt-card'>
      <div className='cmt-card-head'>
        <span>{tryResolveFunction(desc.title, context)}</span>
        {renderHeaderActions()}
      </div>
      <div className='cmt-list'>
        {stateMsg && <div className='cmt-empty'>{stateMsg}</div>}
        {!stateMsg && entries.length === 0 && <div className='cmt-empty'>No entries yet</div>}
        {entries.map((entry, index) => {
          const meta = desc.metaAttribution ? buildAttribution(entry, desc.metaAttribution) : null;
          const body = desc.renderBody(entry, context);
          return (
            <div
              className='cmt-item'
              key={`entry${index}`}
            >
              {meta ? <p className='cmt-meta'>{meta}</p> : null}
              {desc.bodyAsHtml ? (
                <p
                  className='cmt-body'
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              ) : (
                <p className='cmt-body'>{body}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className='comments-panel verticalScroll'>
      {renderAddBox()}
      <Loading loading={loading} />
      {!loading && renderList()}
    </div>
  );
  //#endregion
}
